import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Box,
  Chip,
  TablePagination,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  Avatar,
  LinearProgress,
  Stack,
  Divider
} from '@mui/material';
import ResponsiveTitle from '../components/ResponsiveTitle';
import {
  collection,
  query,
  onSnapshot,
  getDocs,
  doc,
  updateDoc,
  deleteDoc
} from 'firebase/firestore';
import { convertFirestoreDate } from '../utils/dateUtils';
import { db, auth } from '../firebase';
import { User } from '../types/User';
import { VerificationBadge } from '../components/VerificationBadge';
import SearchIcon from '@mui/icons-material/Search';
import PeopleIcon from '@mui/icons-material/People';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import PageLayout from '../components/PageLayout';

const MEMBER_TYPES = {
  ADMIN: 'admin',
  BRONZE: 'bronze',
  SILVER: 'silver',
  GOLD: 'gold'
};

const MEMBER_TYPE_DESCRIPTIONS = {
  [MEMBER_TYPES.ADMIN]: 'Accès complet à tous les événements et fonctionnalités administratives. Peut gérer les utilisateurs et les événements.',
  [MEMBER_TYPES.GOLD]: 'Accès prioritaire à tous les événements (communaux, à domicile et en ligne). Places réservées sur tous les événements.',
  [MEMBER_TYPES.SILVER]: 'Accès aux événements communaux et en ligne. Priorité d’inscription sur les événements communaux.',
  [MEMBER_TYPES.BRONZE]: 'Accès aux événements communaux uniquement. Inscription possible après les membres Silver et Gold.'
};

// --- STATS INTERFACES ---
interface DashboardStats {
  totalUsers: number;
  newUsersThisYear: number;
  activeUsers: number;
  inactiveUsers: number;
  roleDistribution: {
    gold: number;
    silver: number;
    bronze: number;
    admin: number;
  };
}

interface UserWithStats extends User {
  eventCount: number;
  currentYearEventCount: number;
  lastEventDate: string | null;
  participatedEvents: string[];
}

export const AdminUsers = () => {
  const [users, setUsers] = useState<UserWithStats[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserWithStats[]>([]);
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    newUsersThisYear: 0,
    activeUsers: 0,
    inactiveUsers: 0,
    roleDistribution: { gold: 0, silver: 0, bronze: 0, admin: 0 }
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [viewMode, setViewMode] = useState<'active' | 'departed'>('active');
  const [participationFilter, setParticipationFilter] = useState<'all' | 'active_year' | 'inactive_year' | 'never'>('all');

  useEffect(() => {
    const fetchUsersAndStats = async () => {
      try {
        // Récupérer tous les événements pour compter les participations
        const eventsSnapshot = await getDocs(collection(db, 'events'));
        const participationCount: { [key: string]: number } = {};
        const participationDetails: { [key: string]: string[] } = {};
        const currentYearParticipationCount: { [key: string]: number } = {};
        const lastEventDates: { [key: string]: Date } = {};

        // Année en cours
        const currentYear = new Date().getFullYear();

        // Convertir les documents en événements avec dates
        const events = eventsSnapshot.docs.map(doc => {
          const eventData = doc.data();
          return {
            id: doc.id,
            title: eventData.title || 'Sans titre',
            startDate: eventData.startDate ? new Date(eventData.startDate) : null,
            participants: eventData.participants || []
          };
        });

        // Trier les événements par date (du plus récent au plus ancien)
        events.sort((a, b) => {
          if (!a.startDate) return 1;
          if (!b.startDate) return -1;
          return b.startDate.getTime() - a.startDate.getTime();
        });

        // Parcourir les événements triés
        events.forEach(event => {
          if (!event.startDate) return;

          const isCurrentYear = event.startDate.getFullYear() === currentYear;

          event.participants.forEach((userId: string) => {
            // Compter toutes les participations et stocker les détails
            participationCount[userId] = (participationCount[userId] || 0) + 1;

            if (!participationDetails[userId]) participationDetails[userId] = [];
            const eventYear = event.startDate ? event.startDate.getFullYear() : '?';
            participationDetails[userId].push(`${event.title} (${eventYear})`);

            // Compter les participations de l'année en cours
            if (isCurrentYear) {
              currentYearParticipationCount[userId] = (currentYearParticipationCount[userId] || 0) + 1;
            }

            // Enregistrer la date du dernier événement (comme les événements sont triés,
            // la première occurrence d'un utilisateur sera son événement le plus récent)
            if (!lastEventDates[userId] && event.startDate) {
              lastEventDates[userId] = event.startDate;
            }
          });
        });

        // Observer les changements dans la collection users
        const unsubscribe = onSnapshot(
          query(collection(db, 'users')),
          (snapshot) => {
            const usersData = snapshot.docs.map(doc => {
              const userData = doc.data();
              // S'assurer que toutes les propriétés requises sont présentes
              return {
                id: doc.id,
                uid: doc.id,
                firstName: userData.firstName || '',
                lastName: userData.lastName || '',
                email: userData.email || '',
                nickname: userData.nickname || '',
                team: userData.team || '',
                city: userData.city || '',
                role: userData.role || 'bronze',
                verified: userData.verified || false,
                parentId: userData.parentId,
                isChild: userData.isChild,
                // Convertir createdAt en chaîne ISO pour correspondre à l'interface User
                createdAt: convertFirestoreDate(userData.createdAt, new Date())?.toISOString() || new Date().toISOString(),
                // Ajouter les propriétés de comptage d'événements pour UserWithStats
                eventCount: participationCount[doc.id] || 0,
                currentYearEventCount: currentYearParticipationCount[doc.id] || 0,
                lastEventDate: lastEventDates[doc.id] ? lastEventDates[doc.id].toISOString() : null,
                participatedEvents: participationDetails[doc.id] || []
              } as UserWithStats;
            });

            // Mettre l’utilisateur courant (admin) en premier
            const currentUid = auth.currentUser?.uid;
            let orderedUsers = usersData;
            if (currentUid) {
              const idx = orderedUsers.findIndex(u => u.uid === currentUid);
              if (idx > -1) {
                const [me] = orderedUsers.splice(idx, 1);
                orderedUsers.unshift(me);
              }
            }

            // CALCUL DES STATS GLOBALES
            const currentYear = new Date().getFullYear();
            const dashboardStats: DashboardStats = {
              totalUsers: orderedUsers.length,
              newUsersThisYear: 0,
              activeUsers: 0,
              inactiveUsers: 0,
              roleDistribution: { gold: 0, silver: 0, bronze: 0, admin: 0 }
            };

            orderedUsers.forEach(u => {
              // Nouveaux inscrits
              if (new Date(u.createdAt).getFullYear() === currentYear) {
                dashboardStats.newUsersThisYear++;
              }
              // Actifs vs Inactifs
              if (u.currentYearEventCount > 0) {
                dashboardStats.activeUsers++;
              } else {
                dashboardStats.inactiveUsers++;
              }
              // Répartition des rôles
              if (u.role === 'gold') dashboardStats.roleDistribution.gold++;
              else if (u.role === 'silver') dashboardStats.roleDistribution.silver++;
              else if (u.role === 'bronze') dashboardStats.roleDistribution.bronze++;
              else if (u.role === 'admin') dashboardStats.roleDistribution.admin++;
            });

            // Mise à jour des états
            setStats(dashboardStats);
            setUsers(orderedUsers);
            setFilteredUsers(orderedUsers);
            setLoading(false);
          }
        );

        return () => unsubscribe();
      } catch (error) {
        console.error('Erreur lors de la récupération des utilisateurs:', error);
        setLoading(false);
      }
    };

    fetchUsersAndStats();
  }, []);

  useEffect(() => {
    const filtered = users.filter(user => {
      // Filtre sur les "partis"
      const isParti = user.lastName?.toUpperCase() === 'PARTI';
      if (viewMode === 'active' && isParti) return false;
      if (viewMode === 'departed' && !isParti) return false;

      const searchString = searchTerm.toLowerCase();
      const matchesSearch = (
        user.email?.toLowerCase().includes(searchString) ||
        user.firstName?.toLowerCase().includes(searchString) ||
        user.lastName?.toLowerCase().includes(searchString) ||
        user.nickname?.toLowerCase().includes(searchString) ||
        user.team?.toLowerCase().includes(searchString) ||
        user.city?.toLowerCase().includes(searchString)
      );

      if (!matchesSearch) return false;

      // Filtre de participation
      if (participationFilter === 'active_year' && user.currentYearEventCount === 0) return false;
      if (participationFilter === 'inactive_year' && (user.currentYearEventCount > 0 || user.eventCount === 0)) return false;
      if (participationFilter === 'never' && user.eventCount > 0) return false;

      return true;
    });
    setFilteredUsers(filtered);
    setPage(0);
  }, [searchTerm, users, viewMode, participationFilter]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleVerificationChange = async (userId: string, newStatus: boolean) => {
    try {
      console.log('Updating user status:', userId, newStatus);
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        verified: newStatus,
        ...(newStatus ? { role: MEMBER_TYPES.BRONZE } : {})
      });
      console.log('User status updated successfully');
    } catch (error) {
      console.error('Erreur lors de la modification du statut:', error);
      throw error;
    }
  };

  const handleRoleChange = async (userId: string, newRole: 'bronze' | 'silver' | 'gold' | 'admin') => {
    console.log('handleRoleChange called with:', { userId, newRole });

    if (!newRole || typeof newRole !== 'string') {
      console.error('Invalid role value received:', newRole);
      return;
    }

    try {
      console.log('Updating user role to:', newRole);
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        role: newRole
      });
      console.log(`Role updated successfully to ${newRole} for user ${userId}`);
    } catch (error) {
      console.error('Erreur lors de la modification du rôle:', error);
      throw error;
    }
  };

  const handleRejectUser = async (userId: string) => {
    try {
      // Supprimer l'utilisateur de la base de données
      await deleteDoc(doc(db, 'users', userId));
    } catch (error) {
      console.error('Erreur lors du rejet de l\'utilisateur:', error);
    }
  };

  if (loading) {
    return (
      <Container>
        <Typography>Chargement...</Typography>
      </Container>
    );
  }

  return (
    <PageLayout>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        mb: 3,
        bgcolor: 'rgba(0, 0, 0, 0.5)',
        borderRadius: 4,
        px: 3,
        py: 1,
        color: 'white',
        ml: { xs: 9, md: 0 },
        width: { xs: 'calc(100% - 90px)', md: 'fit-content' },
        maxWidth: '100%',
        boxSizing: 'border-box'
      }}>
        <PeopleIcon />
        <ResponsiveTitle variant="h4" minFontSize={16} maxFontSize={34} style={{ fontWeight: 'bold' }}>
          Dashboard Adhérents
        </ResponsiveTitle>
      </Box>

      {/* --- DASHBOARD STATS --- */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Total Users */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            bgcolor: 'rgba(25, 118, 210, 0.15)',
            border: '1px solid rgba(25, 118, 210, 0.3)',
            borderRadius: 2
          }}>
            <Typography variant="subtitle2" color="rgba(255,255,255,0.7)">Total Adhérents</Typography>
            <Typography variant="h3" fontWeight="800" sx={{ mt: 1, mb: 1, color: '#90caf9' }}>
              {stats.totalUsers}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PeopleIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.5)' }} />
              <Typography variant="caption" color="rgba(255,255,255,0.5)">Communauté</Typography>
            </Box>
          </Paper>
        </Grid>

        {/* New Users */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            bgcolor: 'rgba(76, 175, 80, 0.15)',
            border: '1px solid rgba(76, 175, 80, 0.3)',
            borderRadius: 2
          }}>
            <Typography variant="subtitle2" color="rgba(255,255,255,0.7)">Nouveaux ({new Date().getFullYear()})</Typography>
            <Typography variant="h3" fontWeight="800" sx={{ mt: 1, mb: 1, color: '#a5d6a7' }}>
              +{stats.newUsersThisYear}
            </Typography>
            <Typography variant="caption" color="rgba(255,255,255,0.5)">Croissance annuelle</Typography>
          </Paper>
        </Grid>

        {/* Active Users */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            bgcolor: 'rgba(255, 193, 7, 0.15)',
            border: '1px solid rgba(255, 193, 7, 0.3)',
            borderRadius: 2
          }}>
            <Typography variant="subtitle2" color="rgba(255,255,255,0.7)">Actifs cette année</Typography>
            <Typography variant="h3" fontWeight="800" sx={{ mt: 1, mb: 1, color: '#ffe082' }}>
              {stats.activeUsers}
            </Typography>
            <Typography variant="caption" color="rgba(255,255,255,0.5)">
              {stats.totalUsers > 0 ? Math.round((stats.activeUsers / stats.totalUsers) * 100) : 0}% de participation
            </Typography>
          </Paper>
        </Grid>

        {/* Roles Distribution (Mini Graph Visual) */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            bgcolor: 'rgba(156, 39, 176, 0.15)',
            border: '1px solid rgba(156, 39, 176, 0.3)',
            borderRadius: 2
          }}>
            <Typography variant="subtitle2" color="rgba(255,255,255,0.7)">Répartition</Typography>
            <Box sx={{ mt: 2, display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden' }}>
              <Box sx={{ flex: stats.roleDistribution.admin, bgcolor: '#90caf9' }} title="Admin" />
              <Box sx={{ flex: stats.roleDistribution.gold, bgcolor: '#ffb74d' }} title="Gold" />
              <Box sx={{ flex: stats.roleDistribution.silver, bgcolor: '#e0e0e0' }} title="Silver" />
              <Box sx={{ flex: stats.roleDistribution.bronze, bgcolor: '#bcaaa4' }} title="Bronze" />
            </Box>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: 'rgba(255,255,255,0.6)' }}>
              <span>Adm: {stats.roleDistribution.admin}</span>
              <span>G: {stats.roleDistribution.gold}</span>
              <span>S: {stats.roleDistribution.silver}</span>
              <span>B: {stats.roleDistribution.bronze}</span>
            </Box>
          </Paper>
        </Grid>
      </Grid>



      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(e, newMode) => {
            if (newMode) setViewMode(newMode);
          }}
          aria-label="Mode d'affichage"
          sx={{
            height: '56px', // Même hauteur que le TextField standard
            bgcolor: '#333',
            '& .MuiToggleButton-root': {
              color: 'rgba(255, 255, 255, 0.7)',
              borderColor: 'rgba(255, 255, 255, 0.23)',
              textTransform: 'none', // Pour avoir la même casse que le texte normal (ou garder majuscules selon préférence)
              fontWeight: 500,
              fontSize: '0.875rem',
              px: 3,
              '&.Mui-selected': {
                bgcolor: 'primary.main',
                color: '#fff',
                '&:hover': {
                  bgcolor: 'primary.dark',
                }
              },
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)',
              }
            }
          }}
        >
          <ToggleButton value="active" aria-label="Comptes actifs">
            Comptes actifs
          </ToggleButton>
          <ToggleButton value="departed" aria-label="Comptes supprimés">
            Comptes supprimés
          </ToggleButton>
        </ToggleButtonGroup>

        <TextField
          fullWidth
          variant="outlined"
          placeholder="Rechercher un adhérent..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            backgroundColor: '#333',
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.23)',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'primary.main',
              },
            },
            '& input': {
              color: '#fff',
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
              </InputAdornment>
            ),
          }}
        />

        <Select
          value={participationFilter}
          onChange={(e) => setParticipationFilter(e.target.value as any)}
          sx={{
            minWidth: 200,
            bgcolor: '#333',
            color: 'white',
            '.MuiSelect-icon': { color: 'white' },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.23)' }
          }}
        >
          <MenuItem value="all">Tous les adhérents</MenuItem>
          <MenuItem value="active_year">Actifs cette année</MenuItem>
          <MenuItem value="inactive_year">Inactifs cette année</MenuItem>
          <MenuItem value="never">Jamais participé</MenuItem>
        </Select>
      </Box>

      <Stack spacing={2}>
        {filteredUsers
          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          .map((user) => (
            <Paper
              key={user.uid}
              sx={{
                p: 2,
                bgcolor: user.uid === auth.currentUser?.uid ? 'rgba(255, 235, 59, 0.05)' : '#333',
                border: user.uid === auth.currentUser?.uid ? '1px solid rgba(255, 235, 59, 0.3)' : '1px solid transparent',
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              <Grid container alignItems="center" spacing={2}>

                {/* 1. IDENTITY & AVATAR */}
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{
                      bgcolor:
                        user.role === 'admin' ? '#1976d2' :
                          user.role === 'gold' ? '#ffc107' :
                            user.role === 'silver' ? '#bdbdbd' : '#8d6e63',
                      color: user.role === 'gold' ? '#000' : '#fff'
                    }}>
                      {user.firstName[0]}{user.lastName[0]}
                    </Avatar>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#fff', lineHeight: 1.2 }}>
                          {user.firstName} {user.lastName}
                        </Typography>
                        {user.nickname && (
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', fontStyle: 'italic', lineHeight: 1.2 }}>
                            "{user.nickname}"
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'block', lineHeight: 1.2 }}>
                        {user.email}
                      </Typography>
                      {/* Context (Mobile friendly) */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2, rowGap: 0.5, mt: 0 }}>
                        {user.team && (
                          <Typography variant="caption" sx={{ color: '#aaa', lineHeight: 1.2 }}>
                            Equipe: {user.team}
                          </Typography>
                        )}
                        {user.city && (
                          <Typography variant="caption" sx={{ color: '#aaa', lineHeight: 1.2 }}>
                            Ville: {user.city}
                          </Typography>
                        )}
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', lineHeight: 1.2 }}>
                          Inscrit le {format(new Date(user.createdAt), 'dd/MM/yyyy', { locale: fr })}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>

                {/* 2. STATS & ENGAGEMENT */}
                <Grid item xs={12} md={4}>
                  <Box sx={{ px: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" color="rgba(255,255,255,0.7)">Participation</Typography>
                      <Typography variant="caption" fontWeight="bold" color={user.currentYearEventCount > 0 ? '#66bb6a' : 'rgba(255,255,255,0.3)'}>
                        {user.eventCount} événements
                      </Typography>
                    </Box>
                    <Tooltip
                      title={
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                            Historique :
                          </Typography>
                          {(user.participatedEvents || []).length > 0 ? (
                            (user.participatedEvents || []).map((evt, i) => (
                              <div key={i}>{evt}</div>
                            ))
                          ) : (
                            <div>Aucun événement</div>
                          )}
                        </Box>
                      }
                      arrow
                      placement="top"
                    >
                      <Box>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(user.eventCount * 10, 100)} // Scale purely visual
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            bgcolor: 'rgba(255,255,255,0.1)',
                            '& .MuiLinearProgress-bar': {
                              bgcolor: user.currentYearEventCount > 0 ? '#66bb6a' : '#90caf9'
                            }
                          }}
                        />
                      </Box>
                    </Tooltip>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                      <Typography variant="caption" color="rgba(255,255,255,0.4)">
                        {user.currentYearEventCount > 0 ? `+${user.currentYearEventCount} cette année` : 'Inactif cette année'}
                      </Typography>
                      <Typography variant="caption" color="rgba(255,255,255,0.4)">
                        Dernier: {user.lastEventDate ? format(new Date(user.lastEventDate), 'dd/MM/yy', { locale: fr }) : '-'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* 3. STATUS & ACTIONS (Preserved Logic) */}
                <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    {user.parentId ? (
                      <Tooltip title="Membre d'une tribu (Enfant)">
                        <Chip
                          label={`Tribu de ${users.find(u => u.uid === user.parentId)?.nickname || 'Inconnu'}`}
                          sx={{
                            bgcolor: 'rgba(156, 39, 176, 0.7)', // Purple for tribe
                            color: '#fff',
                            fontWeight: 'bold'
                          }}
                          size="small"
                        />
                      </Tooltip>
                    ) : !user.verified ? (
                      <VerificationBadge
                        verified={user.verified}
                        userId={user.uid}
                        onStatusChange={handleVerificationChange}
                      />
                    ) : (
                      <>
                        {user.uid !== auth.currentUser?.uid && user.lastName?.toUpperCase() !== 'PARTI' ? (
                          <Select
                            value={user.role}
                            onChange={(event) => {
                              const role = event.target.value as 'bronze' | 'silver' | 'gold' | 'admin';
                              if (role === MEMBER_TYPES.BRONZE ||
                                role === MEMBER_TYPES.SILVER ||
                                role === MEMBER_TYPES.GOLD ||
                                role === MEMBER_TYPES.ADMIN) {
                                handleRoleChange(user.uid, role);
                              }
                            }}
                            size="small"
                            sx={{
                              minWidth: 120,
                              '.MuiSelect-select': { py: 0.5 },
                              color: '#fff',
                              backgroundColor:
                                user.role === MEMBER_TYPES.ADMIN ? 'rgba(25, 118, 210, 0.7)' :
                                  user.role === MEMBER_TYPES.GOLD ? 'rgba(255, 193, 7, 0.7)' :
                                    user.role === MEMBER_TYPES.SILVER ? 'rgba(158, 158, 158, 0.7)' :
                                      'rgba(205, 127, 50, 0.7)', // bronze
                              '.MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(255, 255, 255, 0.23)',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(255, 255, 255, 0.23)',
                              },
                              '.MuiSvgIcon-root': {
                                color: '#fff',
                              }
                            }}
                          >
                            <MenuItem value={MEMBER_TYPES.BRONZE}
                              sx={{
                                backgroundColor: 'rgba(205, 127, 50, 0.7)',
                                color: '#fff',
                                '&:hover': { backgroundColor: 'rgba(205, 127, 50, 0.85)' },
                                '.MuiSelect-select': { py: 0.5 },
                                pt: 0.5,
                                pb: 0.5
                              }}
                            >
                              <Tooltip title={MEMBER_TYPE_DESCRIPTIONS[MEMBER_TYPES.BRONZE]} placement="right">
                                <Box>Bronze</Box>
                              </Tooltip>
                            </MenuItem>
                            <MenuItem value={MEMBER_TYPES.SILVER}
                              sx={{
                                backgroundColor: 'rgba(158, 158, 158, 0.7)',
                                color: '#fff',
                                '&:hover': { backgroundColor: 'rgba(158, 158, 158, 0.85)' },
                                '.MuiSelect-select': { py: 0.5 },
                                pt: 0.5,
                                pb: 0.5
                              }}
                            >
                              <Tooltip title={MEMBER_TYPE_DESCRIPTIONS[MEMBER_TYPES.SILVER]} placement="right">
                                <Box>Argent</Box>
                              </Tooltip>
                            </MenuItem>
                            <MenuItem value={MEMBER_TYPES.GOLD}
                              sx={{
                                backgroundColor: 'rgba(255, 193, 7, 0.7)',
                                color: '#000',
                                '&:hover': { backgroundColor: 'rgba(255, 193, 7, 0.85)' },
                                '.MuiSelect-select': { py: 0.5 },
                                pt: 0.5,
                                pb: 0.5
                              }}
                            >
                              <Tooltip title={MEMBER_TYPE_DESCRIPTIONS[MEMBER_TYPES.GOLD]} placement="right">
                                <Box>Or</Box>
                              </Tooltip>
                            </MenuItem>
                            <MenuItem value={MEMBER_TYPES.ADMIN}
                              sx={{
                                backgroundColor: 'rgba(25, 118, 210, 0.7)',
                                color: '#fff',
                                '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.85)' },
                                '.MuiSelect-select': { py: 0.5 },
                                pt: 0.5,
                                pb: 0.5
                              }}
                            >
                              <Tooltip title={MEMBER_TYPE_DESCRIPTIONS[MEMBER_TYPES.ADMIN]} placement="right">
                                <Box>Admin</Box>
                              </Tooltip>
                            </MenuItem>
                          </Select>
                        ) : (
                          <Tooltip title={MEMBER_TYPE_DESCRIPTIONS[user.role]} placement="top">
                            <Chip
                              label={user.role.toUpperCase()}
                              sx={{
                                backgroundColor:
                                  user.role === MEMBER_TYPES.ADMIN ? 'rgba(25, 118, 210, 0.7)' :
                                    user.role === MEMBER_TYPES.GOLD ? 'rgba(255, 193, 7, 0.7)' :
                                      user.role === MEMBER_TYPES.SILVER ? 'rgba(158, 158, 158, 0.7)' :
                                        'rgba(205, 127, 50, 0.7)', // bronze
                                color: user.role === MEMBER_TYPES.GOLD ? '#000' : '#fff',
                                fontWeight: 'bold'
                              }}
                              size="small"
                            />
                          </Tooltip>
                        )}
                      </>
                    )}

                    {/* More Actions (Placeholder for future) */}
                    {/* <IconButton size="small" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                          <MoreVertIcon />
                        </IconButton> */}
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          ))}
      </Stack>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={filteredUsers.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          color: '#fff',
          '& .MuiTablePagination-select': {
            color: '#fff'
          },
          '& .MuiTablePagination-selectIcon': {
            color: '#fff'
          }
        }}
        labelRowsPerPage="Lignes par page"
      />
    </PageLayout >
  );
};
